/**
 * MailComposer — Iter Feb 2026 R47.
 *
 * Reusable dialog for sending manual emails to a Lead / Prospect / Client /
 * Contact from anywhere in the app. Rich text body, attachments (drag-drop
 * or click), CC/BCC, per-user signature auto-appended (falls back to
 * workspace default).
 *
 * Props:
 *   open                : boolean
 *   onOpenChange        : (bool) => void
 *   entityType          : "lead" | "prospect" | "client" | "contact"
 *   entityId            : string
 *   defaultTo           : string  — prefills the To field (usually the
 *                                    entity's email address)
 *   defaultSubject      : string
 *   defaultRecipientName: string  — shown as read-only helper text
 *   onSent              : (result) => void   fires on success
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Paperclip, X, Send, Loader2, Upload } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { RichTextEditor } from "@/components/diagrams/RichTextEditor";

const MAX_PER_FILE = 10 * 1024 * 1024;
const MAX_TOTAL = 25 * 1024 * 1024;
const humanSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n/1024).toFixed(0)} KB` : `${(n/1024/1024).toFixed(1)} MB`;

function parseCsvEmails(v) {
  return (v || "").split(/[,\s;]+/).map(s => s.trim()).filter(Boolean);
}

export default function MailComposer({
  open, onOpenChange, entityType, entityId,
  defaultTo = "", defaultSubject = "", defaultRecipientName = "",
  defaultHtml = "",
  defaultMailboxId = null,
  onSent,
}) {
  const { activeWorkspaceId, user } = useAuth();
  const [subject, setSubject] = useState(defaultSubject);
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [html, setHtml] = useState("<p></p>");
  const [signatureHtml, setSignatureHtml] = useState("");
  const [signatureSource, setSignatureSource] = useState(""); // "user" | "workspace" | ""
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{file_id, filename, size}]
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  // R47 Phase-3 · Send-As mailbox picker
  const [mailboxes, setMailboxes] = useState([]);
  const [sendFromMailboxId, setSendFromMailboxId] = useState(defaultMailboxId || "");
  // R69e · Default sender preview (name + email + reply-to) so the sender
  // sees EXACTLY what the recipient will see, per selected option.
  const [defaultSender, setDefaultSender] = useState(null);
  // R69k · Rich email templates from marketing module. Any workspace
  // member can load one to pre-fill subject + body. Recipient context
  // (defaultTo / defaultRecipientName / entityId) drives variable
  // substitution — {{recipient_name}}, {{recipient_email}}, etc.
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templateBusy, setTemplateBusy] = useState(false);

  // Reset form + fetch effective signature whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject);
    setTo(defaultTo);
    setCc(""); setBcc("");
    setShowCc(false); setShowBcc(false);
    setHtml(defaultHtml || "<p></p>");
    setAttachments([]);
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const [meRes, wsRes, mbRes, dsRes, tplRes] = await Promise.all([
          api.get("/me/email-signature"),
          api.get(`/workspaces/${activeWorkspaceId}/email-signature`),
          api.get(`/workspaces/${activeWorkspaceId}/mailboxes`).catch(() => ({ data: { rows: [] } })),
          api.get(`/workspaces/${activeWorkspaceId}/settings/email-provider/preview`).catch(() => ({ data: null })),
          api.get(`/workspaces/${activeWorkspaceId}/email-templates`).catch(() => ({ data: { templates: [] } })),
        ]);
        const me = (meRes.data.signature_html || "").trim();
        const ws = (wsRes.data.signature_html || "").trim();
        if (me) { setSignatureHtml(me); setSignatureSource("user"); }
        else if (ws) { setSignatureHtml(ws); setSignatureSource("workspace"); }
        else { setSignatureHtml(""); setSignatureSource(""); }
        setMailboxes(mbRes.data.rows || []);
        setDefaultSender(dsRes.data || null);
        setEmailTemplates(tplRes.data?.templates || []);
        // If defaultMailboxId is set OR one of user's personal mailboxes
        // exists, default to it so replies come to their own inbox.
        if (defaultMailboxId) setSendFromMailboxId(defaultMailboxId);
        else if (!sendFromMailboxId) {
          const personal = (mbRes.data.rows || []).find(m => m.owner_user_id);
          if (personal) setSendFromMailboxId(personal.id);
        }
      } catch {
        setSignatureHtml(""); setSignatureSource("");
      }
    })();
  }, [open, defaultSubject, defaultTo, defaultHtml, activeWorkspaceId, defaultMailboxId]);

  const totalBytes = useMemo(
    () => attachments.reduce((s, a) => s + (a.size || 0), 0),
    [attachments],
  );

  // R69k · Load a template — fetches rendered subject + html with the
  // recipient's name/email injected as variables, then populates the
  // composer. Overwrites current subject/html if user confirms.
  const loadTemplate = async (templateId) => {
    if (!templateId) return;
    if ((subject.trim() || (html && html !== "<p></p>")) &&
        !window.confirm("Replace current draft with this template?")) return;
    setTemplateBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/email-templates/render`, {
        template_id: templateId,
        variables: {
          recipient_name: defaultRecipientName || "",
          recipient_email: defaultTo || to || "",
          sender_name: user?.name || "",
        },
      });
      if (data.subject) setSubject(data.subject);
      if (data.html) setHtml(data.html);
      toast.success("Template loaded — review and edit before sending");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load template"));
    } finally {
      setTemplateBusy(false);
    }
  };

  const senderPreview = useMemo(() => {
    const userName = user?.name || user?.email || "";
    if (sendFromMailboxId) {
      const mb = mailboxes.find((m) => m.id === sendFromMailboxId);
      if (!mb) return null;
      const mbAddr = mb.imap_username || mb.email || mb.smtp_username || "";
      const fromName = userName ? `${userName}${mb.name ? " · " + mb.name : ""}` : (mb.name || "");
      return {
        source: "mailbox",
        from_display: fromName || mbAddr,
        from_email: mbAddr,
        reply_to: mbAddr,
        note: "Recipient sees your real mailbox address. Replies land in your mailbox.",
      };
    }
    // Workspace default sender
    const senderName = defaultSender?.sender_name || "Workspace";
    const senderEmail = defaultSender?.sender_email || "(platform default)";
    return {
      source: "workspace",
      from_display: userName ? `${userName} · ${senderName}` : senderName,
      from_email: senderEmail,
      reply_to: user?.email || "(your account email)",
      note: `Sent via workspace ${defaultSender?.provider || "platform"} provider. Replies come to your account email.`,
    };
  }, [sendFromMailboxId, mailboxes, defaultSender, user]);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    if (!activeWorkspaceId) { toast.error("Workspace not ready"); return; }
    setUploading(true);
    try {
      const next = [...attachments];
      for (const f of Array.from(files)) {
        if (f.size > MAX_PER_FILE) { toast.error(`${f.name} > 10 MB`); continue; }
        if (next.reduce((s, a) => s + a.size, 0) + f.size > MAX_TOTAL) {
          toast.error("Attachments total > 25 MB — remove one first");
          break;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(
          `/workspaces/${activeWorkspaceId}/files/upload?scope=mail_attachment`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        next.push({ file_id: data.id, filename: data.original_filename || f.name, size: data.size || f.size });
      }
      setAttachments(next);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    uploadFiles(e.dataTransfer?.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!dragActive) setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };

  const removeAttachment = (idx) =>
    setAttachments(attachments.filter((_, i) => i !== idx));

  const previewSendToSelf = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!html || html === "<p></p>") { toast.error("Body is empty"); return; }
    const selfEmail = user?.email;
    if (!selfEmail) { toast.error("No email on your account"); return; }
    setBusy(true);
    try {
      const previewSubject = "[PREVIEW] " + subject.trim();
      const payload = {
        to: selfEmail,
        subject: previewSubject,
        html,
        cc: [],
        bcc: [],
        attachments: attachments.map(a => ({ file_id: a.file_id })),
        mailbox_id: sendFromMailboxId || null,
      };
      await api.post(`/workspaces/${activeWorkspaceId}/mail/send`, payload);
      toast.success(`Preview sent to ${selfEmail} — check your inbox`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview send failed"));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!html || html === "<p></p>") { toast.error("Body is empty"); return; }
    if (!to.trim()) { toast.error("Recipient email is required"); return; }
    setBusy(true);
    try {
      const payload = {
        to: to.trim(),
        entity_type: entityType,
        entity_id: entityId,
        subject: subject.trim(),
        html,
        cc: parseCsvEmails(cc),
        bcc: parseCsvEmails(bcc),
        attachments: attachments.map(a => ({ file_id: a.file_id })),
        mailbox_id: sendFromMailboxId || null,
      };
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/mail/send`, payload);
      if (data.queued) {
        // R69f · Auto-updating toast: show loading now, poll for the
        // final status every 5s (max 12 attempts = 60s), and swap the
        // toast to success/error once the background settles. Poll is
        // bound to the workspace id captured at send time so a mid-poll
        // workspace switch doesn't leak requests to the new ws.
        const toastId = `mail-queued-${data.id}`;
        const ctrl = new AbortController();
        toast.loading("Sending in background — SMTP relay is slow", { id: toastId, duration: 65000 });
        _pollQueuedStatus({
          wsId: activeWorkspaceId, mid: data.id, toastId, provider: data.provider,
          signal: ctrl.signal,
        });
      } else {
        toast.success(`Email sent via ${data.provider || "mail server"}`);
      }
      onOpenChange?.(false);
      onSent?.(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Email send failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none"
        data-testid="mail-composer"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Send className="w-4 h-4" /> New email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* R47 Phase-3 · Send-As picker */}
          {mailboxes.length > 0 && (
            <div className="flex items-center gap-2">
              <Label className="text-xs w-12 shrink-0">From</Label>
              <select
                value={sendFromMailboxId}
                onChange={(e) => setSendFromMailboxId(e.target.value)}
                className="h-9 border border-border px-2 text-sm bg-background rounded-none flex-1"
                data-testid="mail-from-mailbox"
              >
                <option value="">Workspace default (system sender)</option>
                {mailboxes.map(m => (
                  <option key={m.id} value={m.id}>
                    Your mailbox: {m.name}{m.owner_user_id ? " · personal" : " · shared"}
                  </option>
                ))}
              </select>
              <span className="text-[10px] text-muted-foreground max-w-[220px]">
                {sendFromMailboxId
                  ? "Sent via your mailbox's SMTP — recipient sees your real address · reply comes to you."
                  : "Uses the workspace email provider."}
              </span>
            </div>
          )}

          {/* R69e · Sender preview — shows exactly what the recipient
              will see: From display name, From email, Reply-To. Gives
              the sender clarity when choosing between mailbox and
              workspace-default routing. */}
          {senderPreview && (
            <div
              className="ml-14 -mt-1 border border-border bg-muted/30 px-3 py-2 text-[11px] space-y-1"
              data-testid="mail-sender-preview"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>Recipient will see</span>
                <span className={`px-1.5 py-0.5 text-[9px] ${senderPreview.source === "mailbox" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>
                  {senderPreview.source === "mailbox" ? "Your mailbox" : "Workspace default"}
                </span>
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-x-3 gap-y-0.5 font-mono">
                <span className="text-muted-foreground">From:</span>
                <span data-testid="preview-from-display">
                  <strong>{senderPreview.from_display}</strong>
                  {senderPreview.from_email && senderPreview.from_email !== senderPreview.from_display && (
                    <span className="text-muted-foreground"> &lt;{senderPreview.from_email}&gt;</span>
                  )}
                </span>
                <span className="text-muted-foreground">Reply-To:</span>
                <span data-testid="preview-reply-to">{senderPreview.reply_to}</span>
              </div>
              <div className="text-[10px] text-muted-foreground italic">{senderPreview.note}</div>
            </div>
          )}

          {/* To / CC / BCC */}
          <div>
            <div className="flex items-center gap-2">
              <Label className="text-xs w-12 shrink-0">To</Label>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="h-9 rounded-none flex-1 text-sm"
                placeholder="recipient@example.com"
                data-testid="mail-to"
              />
              <div className="flex gap-1 text-[11px]">
                {!showCc && <button onClick={() => setShowCc(true)} className="underline text-muted-foreground hover:text-foreground" data-testid="mail-add-cc">Cc</button>}
                {!showBcc && <button onClick={() => setShowBcc(true)} className="underline text-muted-foreground hover:text-foreground" data-testid="mail-add-bcc">Bcc</button>}
              </div>
            </div>
            {defaultRecipientName && (
              <div className="text-[10px] text-muted-foreground pl-14 mt-0.5">{defaultRecipientName}</div>
            )}
          </div>
          {showCc && (
            <div className="flex items-center gap-2">
              <Label className="text-xs w-12 shrink-0">Cc</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} className="h-9 rounded-none flex-1 text-sm" placeholder="Comma-separated emails" data-testid="mail-cc" />
              <button onClick={() => { setShowCc(false); setCc(""); }} className="text-muted-foreground hover:text-rose-600"><X className="w-3 h-3" /></button>
            </div>
          )}
          {showBcc && (
            <div className="flex items-center gap-2">
              <Label className="text-xs w-12 shrink-0">Bcc</Label>
              <Input value={bcc} onChange={(e) => setBcc(e.target.value)} className="h-9 rounded-none flex-1 text-sm" placeholder="Comma-separated emails" data-testid="mail-bcc" />
              <button onClick={() => { setShowBcc(false); setBcc(""); }} className="text-muted-foreground hover:text-rose-600"><X className="w-3 h-3" /></button>
            </div>
          )}

          {/* R69k · Rich Email Template picker — loads a saved
              template's subject + html into the composer. Populated
              from marketing_templates where channel='email'. Any
              workspace member sees it; templates come from the
              Marketing → Email Templates module. */}
          {emailTemplates.length > 0 && (
            <div className="flex items-center gap-2">
              <Label className="text-xs w-12 shrink-0">Template</Label>
              <select
                onChange={(e) => { loadTemplate(e.target.value); e.target.value = ""; }}
                disabled={templateBusy}
                className="h-9 border border-border px-2 text-sm bg-background rounded-none flex-1"
                data-testid="mail-template-picker"
                defaultValue=""
              >
                <option value="">— Load a saved template (optional) —</option>
                {emailTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || t.subject || "Untitled template"}
                    {t.tags && t.tags.length ? ` · ${t.tags.slice(0, 2).join(", ")}` : ""}
                  </option>
                ))}
              </select>
              <span className="text-[10px] text-muted-foreground max-w-[220px]">
                Overwrites subject + body. Available variables: <code>{"{{recipient_name}}"}</code>, <code>{"{{recipient_email}}"}</code>, <code>{"{{sender_name}}"}</code>.
              </span>
            </div>
          )}

          {/* Subject */}
          <div className="flex items-center gap-2">
            <Label className="text-xs w-12 shrink-0">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 rounded-none flex-1 text-sm" placeholder="Subject line" data-testid="mail-subject" />
          </div>

          {/* Body */}
          <div>
            <RichTextEditor value={html} onChange={setHtml} placeholder="Write your message…" />
          </div>

          {/* Signature preview */}
          {signatureHtml && (
            <div className="border-t pt-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Signature <span className="normal-case tracking-normal">({signatureSource === "user" ? "your personal" : "workspace default"})</span>
              </div>
              <div className="text-xs text-neutral-600 border-l-2 border-neutral-300 pl-2" dangerouslySetInnerHTML={{ __html: signatureHtml }} />
            </div>
          )}

          {/* Attachments */}
          <div className="border-t pt-2">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <Paperclip className="inline w-3 h-3 mr-1" />Attachments · {attachments.length} · {humanSize(totalBytes)}
              </div>
              <div className="text-[10px] text-muted-foreground">Max 10 MB per file · 25 MB total</div>
            </div>
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed p-2 text-center cursor-pointer text-xs transition-colors ${
                dragActive ? "border-sky-500 bg-sky-50" : "border-neutral-300 hover:border-neutral-500"
              }`}
              data-testid="mail-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
                data-testid="mail-file-input"
              />
              {uploading ? (
                <span className="flex items-center justify-center gap-1 text-sky-700"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>
              ) : (
                <span className="flex items-center justify-center gap-1 text-muted-foreground">
                  <Upload className="w-3 h-3" /> Drop files or click to add attachments
                </span>
              )}
            </div>
            {attachments.length > 0 && (
              <div className="mt-1.5 space-y-1">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center justify-between border px-2 py-1 text-xs" data-testid={`mail-attachment-${i}`}>
                    <span className="flex items-center gap-1 truncate">
                      <Paperclip className="w-3 h-3" /> {a.filename}
                    </span>
                    <span className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      {humanSize(a.size)}
                      <button onClick={() => removeAttachment(i)} className="text-rose-600 hover:text-rose-800" data-testid={`mail-attachment-remove-${i}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy} className="rounded-none" data-testid="mail-cancel">Cancel</Button>
          <Button
            variant="outline"
            onClick={previewSendToSelf}
            disabled={busy || uploading || !subject.trim() || !html || html === "<p></p>" || !user?.email}
            className="rounded-none mr-auto"
            title={user?.email ? `Send a copy to yourself (${user.email})` : "No email on account"}
            data-testid="mail-preview-send"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" /> Send preview to me
          </Button>
          <Button onClick={send} disabled={busy || uploading || !subject.trim() || !to.trim()} className="rounded-none" data-testid="mail-send">
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send email</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// R69f · Poll the queued-status endpoint every 5s (max 12 tries) after a
// send that returned `{queued: true}`. Updates the same toast id via
// sonner's `toast.loading` → success/error transition so the user sees
// the outcome without refreshing. Accepts an AbortSignal so a caller
// can cancel in-flight polls (e.g. workspace switch).
async function _pollQueuedStatus({ wsId, mid, toastId, provider, attempts = 0, signal }) {
  const MAX = 12;      // ~60s total
  const DELAY = 5000;  // 5s between polls
  if (signal?.aborted) return;
  if (attempts >= MAX) {
    toast.info("Still sending in background — open the History tab to check delivery.", { id: toastId, duration: 6000 });
    return;
  }
  await new Promise((r) => setTimeout(r, DELAY));
  if (signal?.aborted) return;
  try {
    const { data } = await api.get(`/workspaces/${wsId}/mail/queued-status/${encodeURIComponent(mid)}`, { signal });
    if (data.settled) {
      const isSent = data.in_memory?.sent || data.log_status === "sent";
      if (isSent) {
        // R69h · Explicit short duration so the success toast auto-dismisses
        // instead of inheriting the 65s loading duration. Sonner keeps the
        // loading's duration on `id` swap unless we set it here.
        toast.success(`Email sent via ${provider || "mailbox"} (took ${5 * (attempts + 1)}s)`,
                        { id: toastId, duration: 4000 });
      } else {
        const err = data.in_memory?.error || "SMTP send failed after retry";
        toast.error(`Email failed: ${err}`, { id: toastId, duration: 8000 });
      }
      return;
    }
  } catch (e) {
    if (e.name === "CanceledError" || e.name === "AbortError") return;
    // Ignore transient errors — keep polling until MAX attempts.
  }
  _pollQueuedStatus({ wsId, mid, toastId, provider, attempts: attempts + 1, signal });
}
